import { useAddMentorCommentToFlag } from '@actions';
import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import Button from '@components/V4/Button';
import { PaginationProps } from '@components/V4/Pagination';
import Table from '@components/V4/Table/Table';
import Tooltip from '@components/V4/Tooltip';
import { Flag, FLAG_STATUS, FlagTypes } from '@shared/common';
import { BASE_URL } from '@shared/frontendEnv';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';
import { memo, useCallback, useMemo, useState } from 'react';
import AddCommentModal from './AddCommentModal';

interface MyFlagsDesktopTableProps {
  refetch: () => void;
  flags?: Flag[];
  pagination: PaginationProps;
  isLoading: boolean;
}

interface MyFlagsTableRow {
  flagData: any;
  addCommentHandler: () => void;
  viewHandler: () => void;
}

const FlagsTableRow = memo(({ flagData, addCommentHandler, viewHandler }: MyFlagsTableRow) => {
  const getStudentName = () => {
    return (
      flagData?.ref?.students?.[0]?.fullName ??
      flagData?.ref?.user?.fullName ??
      `${flagData?.ref?.firstName} ${flagData?.ref?.lastName}`
    );
  };

  return (
    <Table.Row>
      <Table.Cell className="flex items-center gap-2">
        <span>{flagData?.description ?? '-'}</span>
      </Table.Cell>
      <Table.Cell>{getStudentName()}</Table.Cell>
      <Table.Cell>{flagData?.ref?.course?.title ?? '-'}</Table.Cell>
      <Table.Cell>
        {flagData?.createdAt ? moment(flagData?.createdAt).format('DD.MM.YYYY') : '-'}
      </Table.Cell>
      <Table.Cell>{FLAG_STATUS[flagData?.status]}</Table.Cell>
      <Table.Cell>
        {flagData?.mentorComment ? (
          <Tooltip
            text={flagData.mentorComment}
            style={{
              maxWidth: '300px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <span className="block max-w-[200px] truncate cursor-pointer">
              {flagData.mentorComment}
            </span>
          </Tooltip>
        ) : (
          <span className="text-customGrey">-</span>
        )}
      </Table.Cell>
      <Table.Cell className="flex gap-4">
        <Button variant="secondary" onClick={() => viewHandler()} buttonText="View" />

        {!flagData?.closedAt && !flagData?.mentorComment && (
          <div onClick={addCommentHandler} className="flex cursor-pointer gap-1">
            <Button variant="primary" buttonText="Add Comment" />
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
});

const MyFlagsDesktopTable = ({
  flags,
  refetch,
  pagination,
  isLoading,
}: MyFlagsDesktopTableProps) => {
  const [selectedFlag, setSelectedFlag] = useState<Flag | null>(null);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);

  const flagId = selectedFlag?._id;
  const { mutate: addComment, isLoading: isSaving } = useAddMentorCommentToFlag(flagId as string);

  const openModal = useCallback(
    (flagId: string) => {
      const flag = flags?.find((f) => f._id === flagId) || null;
      setSelectedFlag(flag);
      setIsAddCommentModalOpen(true);
    },
    [flags]
  );

  const closeModal = useCallback(() => {
    setIsAddCommentModalOpen(false);
    setSelectedFlag(null);
  }, []);

  const handleSave = useCallback(
    (comment: string) => {
      if (!selectedFlag) return;
      addComment({ comment } as any, {
        onSuccess: () => {
          closeModal();
          refetch();
        },
        onError: () => {},
      });
    },
    [addComment, closeModal, refetch, selectedFlag]
  );

  const viewFlag = useCallback((flag: Flag) => {
    switch (flag.flagType) {
      case FlagTypes.FIRST_MEETING_NOT_SCHEDULED_IN_5_DAYS:
        window.location.href = `${BASE_URL}/session-details/${
          (flag.ref as any)?.sessions?.[0]?._id
        }`;
        break;
      case FlagTypes.FIRST_MEETING_NOT_HELD_IN_10_DAYS:
        window.location.href = `${BASE_URL}/session-details/${
          (flag.ref as any)?.sessions?.[0]?._id
        }`;
        break;
      case FlagTypes.NEXT_MEETING_FURTHER_THAN_62_DAYS:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;
      case FlagTypes.NO_MEETING_SCHEDULED_AFTER_48_HOURS_SIENCE_LAST_MEETING_HELD:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;
      case FlagTypes.NO_FEEDBACK_AFTER_48_HOURS_SINCE_MEETING_HELD:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;
      case FlagTypes.MAX_DURATION_COURSE_EXCEEDED:
        window.location.href = `${BASE_URL}/my-enrollments?page=1&limit=10&course=${
          (flag.ref as any)?.course?._id
        }&studentSearch=${(flag.ref as any)?.user?.fullName}`;
        break;

      case FlagTypes.PAST_MEETING_WITH_INVALID_STATUS:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;

      case FlagTypes.ENROLLMENT_NOT_ENDED_AFTER_LAST_SESSION:
        window.location.href = `${BASE_URL}/my-enrollments?page=1&limit=10&course=${
          (flag.ref as any)?.course?._id
        }&studentSearch=${(flag.ref as any)?.students[0]?.fullName}`;
        break;

      case FlagTypes.BILLABLE_TIME_LESS_THAN_50_MINUTES:
        window.location.href = `${BASE_URL}/session-details/${(flag.ref as any)?.sessionId}`;
        break;

      default:
        console.warn('Unknown flag type:', flag.flagType);
        break;
    }
  }, []);

  const rows = useMemo(
    () =>
      flags?.map((flag) => (
        <FlagsTableRow
          key={flag._id}
          flagData={flag}
          addCommentHandler={() => openModal(flag._id)}
          viewHandler={() => viewFlag(flag)}
        />
      )),
    [flags, openModal]
  );

  return (
    <>
      <Table
        isLoading={isLoading}
        isUserView
        className="hidden w-full tablet:table"
        pagination={pagination}
      >
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Student</Table.HeaderCell>
            <Table.HeaderCell>Course</Table.HeaderCell>
            <Table.HeaderCell>Created At</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Comment</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>{' '}
        </Table.Header>
        <Table.Body>
          {!isLoading && !flags?.length && (
            <tr>
              <td colSpan={6} className="h-64">
                <EmptyStateCard
                  icon={<BlockIcon className="text-customGrey" />}
                  text="No results."
                />
              </td>
            </tr>
          )}
          {rows}
        </Table.Body>
      </Table>

      <AddCommentModal
        isOpen={isAddCommentModalOpen}
        flag={selectedFlag}
        saving={isSaving}
        onClose={closeModal}
        onSave={handleSave}
      />
    </>
  );
};

export default MyFlagsDesktopTable;
